/**
 * Which segments this shoot's call sheet actually publishes.
 *
 * Added 13 Sep 2026, at Tom's request: "we have a live broadcast tomorrow
 * and the schedule is very important but kit and shot types is not."
 *
 * Only the optional extras get a switch. Schedule, Crew, Client & Guests,
 * Locations & Access and Important Instructions are always published — a
 * call sheet without them isn't a call sheet, and a switch that can produce
 * an empty page is a footgun rather than a feature.
 *
 * Switching a segment off never deletes anything. Staff keep editing it in
 * the builder (greyed, marked Off); it simply doesn't reach the crew link,
 * the client link or the PDF. That distinction is the whole point: a live
 * broadcast doesn't need the kit list, but the same project's studio shoot
 * next month will, and nobody should have to retype it.
 *
 * The switches live on the section headings themselves — see SectionSwitch in
 * components/portal/CallSheetSectionSwitches.tsx. They were first built as a
 * single "Include On This Call Sheet" row at the top of the builder, which
 * Tom asked to drop: on a page this long a control at the top governing a
 * section two thirds of the way down is a control nobody finds when they're
 * standing in front of the thing they want to switch off.
 */
export interface CallSheetSections {
  shots: boolean
  kit: boolean
  /** The four file links (script, bios, storyboard, Dropbox). NOT Important
      Instructions, which travels in the same payload but is always shown. */
  files: boolean
}

/** What every shoot day has had since before the switches existed, and what
    a new one starts as — nothing changes behaviour until Tom turns it off. */
export const ALL_SECTIONS_ON: CallSheetSections = { shots: true, kit: true, files: true }

export type CallSheetSectionKey = keyof CallSheetSections

/** Tolerates a payload from before the switches existed (or a null from a
    partial read) by treating anything unstated as on. */
export function readSections(value: Partial<CallSheetSections> | null | undefined): CallSheetSections {
  return {
    shots: value?.shots !== false,
    kit: value?.kit !== false,
    files: value?.files !== false,
  }
}
